import { useContext, useEffect, useState } from 'react';
import { Button, Dialog } from '@schibsted-svp/react-ui';

import { fetchSharedComponents } from '../../services/api/components';
import { CenteredSpinner } from '../CenteredSpinner/CenteredSpinner';
import { PageContext } from '../../contexts/PageContext';

import { Component } from './Component/Component';

import type { ComponentResponse } from '../../types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onPick: (component: ComponentResponse) => void | Promise<void>;
};

function ComponentPicker({ isOpen, onClose, onPick }: Props) {
  const [components, setComponents] = useState<ComponentResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { page } = useContext(PageContext);

  const excludedComponents =
    page && page.components ? page.components.map(({ id }) => id) : [];

  async function fetchComponents() {
    const fetched = await fetchSharedComponents();

    setComponents(fetched);
    setIsLoading(false);
  }

  async function onComponentClick(component: ComponentResponse) {
    await onPick(component);
    onClose();
  }

  useEffect(() => {
    if (isOpen) {
      fetchComponents();
    }
  }, [isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      heading="Select a shared component"
    >
      <Dialog.Section>
        {isLoading && <CenteredSpinner />}
        {components.map((component) => (
          <Component
            key={component.id}
            component={component}
            onClick={onComponentClick}
            isDisabled={excludedComponents.includes(component.id)}
          />
        ))}
      </Dialog.Section>
      <Dialog.Section variant="darker" mode="flexRight">
        <Button type="button" variant="standard" onClick={onClose}>
          Cancel
        </Button>
      </Dialog.Section>
    </Dialog>
  );
}

export { ComponentPicker };
